/**
 * persona-ai-dialog.tsx — "AI tự động tạo" Thiết lập nhân vật: 3 tab
 *   Tạo mới (mô tả + giọng điệu + độ dài) · Tối ưu bản hiện tại · Dùng mẫu (thư viện theo nhóm).
 * Bên phải là bản xem trước — KHÔNG tự lưu: bấm "Dùng bản này" mới chép vào ô soạn (rồi tự lưu nháp).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Loader2, Sparkles, WandSparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { ChipGroup } from './train-ui'
import {
  generatePersona, optimizePersona, usePersonaTemplates, LENGTH_OPTIONS, TONE_OPTIONS,
  type PersonaLength, type PersonaTone,
} from './train-api'

export type PersonaAiTab = 'create' | 'optimize' | 'template'

export function PersonaAiDialog({ open, onOpenChange, botId, current, onApply, initialTab = 'create', initialRequest = '' }: {
  open: boolean
  onOpenChange: (o: boolean) => void
  botId: string
  current: string
  onApply: (text: string) => void
  initialTab?: PersonaAiTab
  /** Yêu cầu tối ưu điền sẵn (vd từ "Kiểm tra chất lượng"). Hộp được mount lại mỗi lần mở. */
  initialRequest?: string
}) {
  const { t } = useTranslation('aiTrainUi')
  const [tab, setTab] = useState<PersonaAiTab>(initialTab)
  const [description, setDescription] = useState('')
  const [tone, setTone] = useState<PersonaTone>('friendly')
  const [length, setLength] = useState<PersonaLength>('standard')
  const [request, setRequest] = useState(initialRequest)
  const [preview, setPreview] = useState('')
  const [busy, setBusy] = useState(false)
  const [group, setGroup] = useState<string>('presale')
  const [templateId, setTemplateId] = useState<string | null>(null)
  const templatesQ = usePersonaTemplates(open)

  const run = async () => {
    setBusy(true)
    try {
      const text = tab === 'create'
        ? await generatePersona({ botId, description, tone, length })
        : await optimizePersona({ botId, current, request })
      setPreview(text)
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setBusy(false)
    }
  }

  const templates = templatesQ.data?.templates ?? []
  const groups = templatesQ.data?.groups ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><WandSparkles className="h-4 w-4 text-primary" /> {t('personaAi.title')}</DialogTitle>
          <DialogDescription>
            {t('personaAi.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          <Tabs value={tab} onValueChange={(v) => { setTab(v as PersonaAiTab); setPreview('') }}>
            <TabsList className="w-full">
              <TabsTrigger value="create" className="flex-1">{t('personaAi.tabCreate')}</TabsTrigger>
              <TabsTrigger value="optimize" className="flex-1">{t('personaAi.tabOptimize')}</TabsTrigger>
              <TabsTrigger value="template" className="flex-1">{t('personaAi.tabTemplate')}</TabsTrigger>
            </TabsList>

            <TabsContent value="create" className="space-y-3 pt-3">
              <div>
                <Label className="text-xs">{t('personaAi.goalLabel')}</Label>
                <Textarea
                  rows={5}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('personaAi.goalPlaceholder')}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('personaAi.toneLabel')}</Label>
                <ChipGroup value={tone} options={TONE_OPTIONS} onChange={setTone} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('personaAi.lengthLabel')}</Label>
                <ChipGroup value={length} options={LENGTH_OPTIONS} onChange={setLength} />
              </div>
              <Button onClick={() => void run()} disabled={busy || description.trim().length < 10} className="w-full">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {preview ? t('personaAi.regenerate') : t('personaAi.generate')}
              </Button>
              {description.trim().length > 0 && description.trim().length < 10 && (
                <p className="text-xs text-muted-foreground">{t('personaAi.describeMore')}</p>
              )}
            </TabsContent>

            <TabsContent value="optimize" className="space-y-3 pt-3">
              {current.trim() ? (
                <>
                  <p className="rounded-md bg-muted/40 p-2 text-xs text-muted-foreground">
                    {t('personaAi.optimizeNote', { variable: '{{…}}' })}
                  </p>
                  <div>
                    <Label className="text-xs">{t('personaAi.requestLabel')}</Label>
                    <Textarea rows={4} value={request} onChange={(e) => setRequest(e.target.value)} placeholder={t('personaAi.requestPlaceholder')} />
                  </div>
                  <Button onClick={() => void run()} disabled={busy} className="w-full">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {preview ? t('personaAi.reoptimize') : t('personaAi.optimize')}
                  </Button>
                </>
              ) : (
                <p className="rounded-md bg-muted/40 p-3 text-sm text-muted-foreground">{t('personaAi.nothingToOptimize')}</p>
              )}
            </TabsContent>

            <TabsContent value="template" className="space-y-3 pt-3">
              <div className="flex flex-wrap gap-1.5">
                {groups.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setGroup(g.id)}
                    className={cn('rounded-full border px-3 py-1 text-xs', group === g.id ? 'border-primary bg-primary/10 font-medium text-primary' : 'hover:bg-muted')}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
              {templatesQ.isLoading && <p className="text-xs text-muted-foreground">{t('personaAi.loadingTemplates')}</p>}
              <div className="max-h-[46vh] space-y-2 overflow-y-auto pr-1">
                {templates.filter((tpl) => tpl.group === group).map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => { setTemplateId(tpl.id); setPreview(tpl.content) }}
                    className={cn('w-full rounded-lg border p-3 text-left transition-colors', templateId === tpl.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/50')}
                  >
                    <p className="text-sm font-medium">{tpl.name}</p>
                    <p className="text-[11px] text-primary">{tpl.industry}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{tpl.summary}</p>
                  </button>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex min-h-[320px] flex-col gap-2">
            <Label className="text-xs">{t('personaAi.preview')} {preview && <span className="font-normal text-muted-foreground">{t('personaAi.previewEditable')}</span>}</Label>
            {preview ? (
              <textarea
                className="min-h-[320px] w-full flex-1 resize-none rounded-md border bg-transparent p-3 font-mono text-xs outline-none focus:ring-1 focus:ring-ring"
                value={preview}
                onChange={(e) => setPreview(e.target.value)}
              />
            ) : (
              <div className="flex flex-1 items-center justify-center rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t('personaAi.draftHere')}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>{t('common:actions.close')}</Button>
              <Button
                disabled={!preview.trim() || busy}
                onClick={() => {
                  if (current.trim() && tab !== 'optimize' && !window.confirm(t('personaAi.confirmReplace'))) return
                  onApply(preview)
                  onOpenChange(false)
                  toast.success(t('personaAi.applied'))
                }}
              >
                {t('personaAi.useThis')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
